import { useMemo, useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { QUIZ_BY_LESSON } from '../../../gql/queries/academyQuery'
import { SUBMIT_QUIZ_ATTEMPT } from '../../../gql/mutations/academyMutations'
import {
  ILesson,
  IQuizByLessonResponse,
  ISubmitQuizAttemptResponse,
  IUserQuizAttempt,
} from '../../../types/academy'

interface IQuizModalProps {
  isOpen: boolean
  onClose: () => void
  lesson: ILesson
  onPassed?: () => void
}

export const QuizModal: React.FC<IQuizModalProps> = ({ isOpen, onClose, lesson, onPassed }) => {
  // {questionId: [selectedOptionIds]}
  const [answers, setAnswers] = useState<Record<string, string[]>>({})
  const [result, setResult] = useState<IUserQuizAttempt | null>(null)
  const [correctAnswers, setCorrectAnswers] = useState<Record<string, number[]> | null>(null)

  const { data, loading } = useQuery<IQuizByLessonResponse>(QUIZ_BY_LESSON, {
    variables: { lessonId: Number(lesson.id) },
    fetchPolicy: 'network-only',
    skip: !isOpen,
  })

  const quiz = data?.quizByLesson
  const questions = useMemo(() => quiz?.questions || [], [quiz])

  const [submitQuizAttempt, { loading: submitting }] =
    useMutation<ISubmitQuizAttemptResponse>(SUBMIT_QUIZ_ATTEMPT)

  const handleSelect = (questionId: string, optionId: string) => {
    // v1: todas las preguntas son de respuesta única (radio)
    setAnswers((prev) => ({ ...prev, [questionId]: [optionId] }))
  }

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id]?.length)

  const handleSubmit = async () => {
    if (!quiz) return
    if (!allAnswered) {
      toast.warning('Please answer all questions before submitting')
      return
    }

    try {
      const payload: Record<string, number[]> = {}
      Object.entries(answers).forEach(([questionId, optionIds]) => {
        payload[questionId] = optionIds.map((id) => Number(id))
      })

      const response = await submitQuizAttempt({
        variables: {
          quizId: Number(quiz.id),
          answers: JSON.stringify(payload),
        },
      })

      const dataResult = response.data?.submitQuizAttempt
      if (dataResult?.success && dataResult.attempt) {
        setResult(dataResult.attempt)
        if (dataResult.correctAnswers) {
          try {
            setCorrectAnswers(JSON.parse(dataResult.correctAnswers))
          } catch {
            setCorrectAnswers(null)
          }
        }
        if (dataResult.attempt.passed) {
          toast.success(`You passed with ${dataResult.attempt.scorePercentage}%!`)
          onPassed?.()
        } else {
          toast.warning(`Score: ${dataResult.attempt.scorePercentage}%. Try again!`)
        }
      } else if (dataResult?.errors?.length) {
        toast.error(`Error: ${dataResult.errors[0].message}`)
      }
    } catch (err) {
      console.error('Error submitting quiz:', err)
      toast.error('Error submitting quiz')
    }
  }

  const handleClose = () => {
    setAnswers({})
    setResult(null)
    setCorrectAnswers(null)
    onClose()
  }

  const handleRetry = () => {
    setAnswers({})
    setResult(null)
    setCorrectAnswers(null)
  }

  const isOptionCorrect = (questionId: string, optionId: string) =>
    correctAnswers?.[questionId]?.includes(Number(optionId)) ?? false

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-patch-question" />
          {quiz?.title || `Quiz: ${lesson.displayTitle}`}
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : !quiz || questions.length === 0 ? (
          <div className="alert alert-info mb-0">
            <i className="bi bi-info-circle me-2" />
            This quiz has no questions yet.
          </div>
        ) : result ? (
          /* Result view */
          <div>
            <div className={`alert ${result.passed ? 'alert-success' : 'alert-danger'} text-center`}>
              <i
                className={`bi ${result.passed ? 'bi-trophy' : 'bi-x-circle'} d-block mb-2`}
                style={{ fontSize: 40 }}
              />
              <h4 className="mb-1">{result.passed ? 'Congratulations!' : 'Not passed'}</h4>
              <p className="mb-0">
                Your score: <strong>{result.scorePercentage}%</strong> (minimum to pass:{' '}
                {quiz.passingScorePercentage}%)
              </p>
            </div>

            {/* Review de respuestas */}
            {correctAnswers && (
              <div className="mt-3">
                <h6 className="mb-3">Review</h6>
                {questions.map((question, idx) => {
                  const selected = answers[question.id]?.[0]
                  const wasCorrect = selected && isOptionCorrect(question.id, selected)
                  return (
                    <div key={question.id} className="card mb-2">
                      <div className="card-body py-2">
                        <div className="d-flex align-items-start gap-2">
                          <i
                            className={`bi mt-1 ${
                              wasCorrect
                                ? 'bi-check-circle-fill text-success'
                                : 'bi-x-circle-fill text-danger'
                            }`}
                          />
                          <div className="flex-fill">
                            <div className="fw-semibold">
                              {idx + 1}. {question.text}
                            </div>
                            <div className="small mt-1">
                              {question.options.map((option) => {
                                const isSelected = selected === option.id
                                const isCorrect = isOptionCorrect(question.id, option.id)
                                if (!isSelected && !isCorrect) return null
                                return (
                                  <div
                                    key={option.id}
                                    className={
                                      isCorrect ? 'text-success' : 'text-danger'
                                    }
                                  >
                                    <i
                                      className={`bi me-1 ${
                                        isCorrect ? 'bi-check' : 'bi-x'
                                      }`}
                                    />
                                    {option.text}
                                    {isSelected && ' (your answer)'}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ) : (
          /* Quiz-taking view: todas las preguntas en una sola página */
          <Form>
            <div className="alert alert-light border small">
              <i className="bi bi-info-circle me-2" />
              Answer all {questions.length} question{questions.length === 1 ? '' : 's'}. Minimum
              score to pass: <strong>{quiz.passingScorePercentage}%</strong>
            </div>
            {questions.map((question, idx) => (
              <div key={question.id} className="card mb-3">
                <div className="card-body">
                  <div className="fw-semibold mb-2">
                    {idx + 1}. {question.text}
                  </div>
                  {question.options.map((option) => (
                    <Form.Check
                      key={option.id}
                      type="radio"
                      id={`q${question.id}-o${option.id}`}
                      name={`question-${question.id}`}
                      label={option.text}
                      checked={answers[question.id]?.[0] === option.id}
                      onChange={() => handleSelect(question.id, option.id)}
                      className="mb-1"
                    />
                  ))}
                </div>
              </div>
            ))}
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Close
        </Button>
        {result ? (
          !result.passed && (
            <Button variant="primary" onClick={handleRetry}>
              <i className="bi bi-arrow-repeat me-1" /> Try Again
            </Button>
          )
        ) : (
          quiz &&
          questions.length > 0 && (
            <Button variant="primary" onClick={handleSubmit} disabled={submitting || !allAnswered}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <i className="bi bi-send me-1" /> Submit Quiz
                </>
              )}
            </Button>
          )
        )}
      </Modal.Footer>
    </Modal>
  )
}
